import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { TripInfoComponent, type TripPackageDetails } from '../types';
import TripSegment from '../TripSegment';
import TripPackage from '../TripPackage';
import TrainPriceType from './TrainPriceType';
import TrainPriceClass from './TrainPriceClass';
import Train from './Train';
import ItineraryOrder from '@itinerary/ItineraryOrder';
import {
    ExtraInfoIcons,
    ExtraInfoLang,
    type CardLabel,
    type PassengerCount,
} from '@itinerary/types';
import { ReserveType } from '@utils/itinerary';
import { TripType } from '@utils/trip';
import { type SellType, SellTypeCode, trainSellTypes } from '@utils/train';
import { Gender } from '@store/form';
import { type AvailableTrain } from '@clients/TrainApi';

export default class TrainPackage extends TripPackage<Train, TrainPriceClass> {
    override reserveType: ReserveType = ReserveType.Train;

    override get tripPackageDetails(): TripPackageDetails[] {
        return [
            ...super.tripPackageDetails,
            {
                title: 'ایستگاه‌های بین‌راهی',
                component: TripInfoComponent.TrainMidStations,
            },
        ];
    }

    tripType: TripType = TripType.OneWay;

    isForRoundTrip = false;

    override needRefreshInvoiceOnChangeNationality = true;

    /**
     * Points of the trip packages, based on passengers' opinions
     */
    override points: number | null = null;

    override prices: TrainPriceType[] = [];

    override totalCount = 0;

    override segments: TripSegment<Train>[] = [];

    override international = false;

    defaultSellType: SellType | null = null;

    exclusive = false;

    override get gateWayMessage(): string {
        return 'با توجه به حذف توقف در برخی ایستگاه‌های بین‌راهی، پیاده و سوار شدن صرفا در ایستگاه‌هایی که بر روی بلیط ثبت شده امکان‌پذیر است.';
    }

    override get cancellationMessage() {
        return 'قوانین کنسلی مطابق با قوانین راه آهن می‌باشد. طبق دستورالعمل راه‌آهن، وجه بلیط استردادشده اینترنتی تنها به شماره کارتی واریز می‌شود که نام دارنده آن با نام یکی از مسافران مندرج در بلیط مطابقت داشته باشد. در غیر این صورت، امکان کنسلی آنلاین وجود نداشته و مسافر باید برای انجام کنسلی به مراکز حضوری مراجعه کند. این محدودیت از سوی راه‌آهن تعیین شده و خارج از اختیار مِستربلیط است.';
    }

    override key = '';

    override callToReserve = false;

    get totalReserveCount() {
        if (!this.exclusive || !this.defaultPrice?.isCompartment) {
            return this.totalCount;
        }
        return this.defaultPrice.getExclusiveNeedCapacity(this.totalCount);
    }

    get serviceLabel() {
        return 'قطار';
    }

    constructor(
        train?: AvailableTrain,
        totalCount = 0,
        sellTypeCode = 0,
        exclusive = false,
        defaultPriceIndex = 0,
        minPersons = 1,
        priceId = 0,
        tripType: TripType = TripType.OneWay,
        score?: number
    ) {
        super();
        if (!train) {
            return;
        }
        this.concreteType = ReserveType.Train;
        this.key = `${train.id}:${priceId}`;
        this.uniqueId = `${train.id}:${priceId}`;

        this.tripType = tripType;
        this.totalCount = totalCount;
        this.minPersons = minPersons;
        this.prices =
            train.prices?.map(p => {
                const sellType = trainSellTypes.find(
                    t => t.code === p.sellType
                );
                if (!sellType) {
                    throw 0;
                }
                return new TrainPriceType(
                    sellType,
                    p.classes?.map(
                        c =>
                            new TrainPriceClass(
                                c,
                                train.isForeign,
                                sellType ?? [],
                                exclusive,
                                totalCount
                            )
                    ) ?? []
                );
            }) ?? [];

        const currentClass = this.prices.find(
            p => p.sellType.code === sellTypeCode
        );
        if (!currentClass) {
            throw 0;
        }
        this.defaultSellType = currentClass.sellType;

        this.defaultPrice = this.selectedPrice =
            currentClass.classes[defaultPriceIndex];
        this.cancellationTerms =
            this.defaultPrice?.price?.cancellationTermEntries?.map(entry => ({
                title: entry.title ?? '',
                amount: ![null, undefined, -1].includes(entry.percentAmount)
                    ? `٪${entry.percentAmount}`
                    : formatMoney(entry.amount),
                isFull: entry.percentAmount === 100,
                isZero: entry.percentAmount === 0 || entry.amount === 0,
            })) || [];
        this.isForRoundTrip = this.defaultPrice.isForRoundTrip;
        this.international = !!train.isForeign;
        this.exclusive = exclusive;
        this.points = score ?? train.score ?? 0;
        this.facilities = {
            title: 'کوپه',
            list: this.defaultPrice.wagonService.map(
                ({ title, hasService }) => ({
                    title: `${title} ${hasService ? 'دارد' : 'ندارد'}`,
                    check: !!hasService,
                })
            ),
        };
        const trips = [new Train(train, this.defaultPrice)];
        this.segments = [
            new TripSegment(
                trips,
                [...(this.pointOption ? [this.pointOption] : [])],
                'بدون توقف'
            ),
        ];
    }

    private get defaultLabels() {
        const labels: CardLabel[] = [];
        if (this.isForRoundTrip) {
            labels.push({
                title: 'ویژه دوطرفه',
                icon: faAngleRight,
            });
        }
        if (this.defaultPrice?.hasSpecialDiscount) {
            labels.push({
                title: 'تخفیف ویژه',
                icon: faStar,
            });
        }
        return labels;
    }

    override get labels(): CardLabel[] {
        const labels = this.defaultLabels;
        const { shortTitle, compartmentType } = this.defaultPrice?.price || {};
        if (shortTitle) {
            labels.push({
                title: shortTitle,
            });
        } else if (this.defaultPrice?.styledWagonName) {
            labels.push({
                title: this.defaultPrice.styledWagonName,
            });
        }

        const mainColor = {
            backgroundColor: 'var(--blue-100)',
            color: 'var(--blue-700)',
        };

        if (compartmentType) {
            labels.push({
                title: compartmentType,
                ...mainColor,
            });
        }

        labels.push(
            ...(this.defaultPrice?.price?.features?.map(label => ({
                title: label,
            })) || [])
        );
        return labels;
    }

    override get extraInfo() {
        const infoConfig = {
            icon: ExtraInfoIcons.trainClass,
            lang: ExtraInfoLang.fa,
        };
        if (!this.defaultPrice?.price?.class) {
            return undefined;
        }
        return {
            text: this.defaultPrice?.price?.class,
            ...infoConfig,
        };
    }

    override get isFull() {
        return (
            !!this.defaultPrice && this.defaultPrice.capacity < this.totalCount
        );
    }

    override get reservable() {
        if (this.defaultPrice?.capacity === 0) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (this.defaultPrice && !this.defaultPrice.available) {
            return false;
        }
        return true;
    }

    override get detailsMessage() {
        if (this.isForRoundTrip) {
            return 'ویژه دوطرفه: این بلیط مخصوص خریدهایی است که در حالت «رفت و برگشت» انجام می‌شود. با توجه به اعمال تخفیف ویژه دوطرفه، امکان استرداد بلیط به‌صورت یک‌طرفه وجود ندارد.';
        } else {
            return null;
        }
    }

    override get alertText() {
        if (
            this.reservable &&
            this.defaultPrice &&
            this.totalCount !== this.totalReserveCount &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return 'ظرفیت قطار انتخابی کمتر از کوپه دربست است.';
        }
        return super.alertText;
    }

    override get buyTooltip() {
        if (
            this.reservable &&
            this.defaultPrice &&
            this.totalCount !== this.totalReserveCount &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return 'ظرفیت قطار انتخابی کمتر از کوپه دربست است.';
        }
        return '';
    }

    override get showTooltip() {
        return true;
    }

    override get buyText() {
        if (this.defaultPrice?.capacity === 0) {
            return 'تکمیل ظرفیت';
        } else if (
            this.defaultPrice &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return 'ظرفیت ناکافی';
        } else if (this.defaultPrice && !this.defaultPrice.available) {
            return 'قطعی سرور';
        } else if (this.minPersons > this.totalCount) {
            return `حداقل تعداد ${this.minPersons} نفر برای رزرو`;
        } else if (
            this.tripType !== TripType.RoundTrip &&
            this.isForRoundTrip
        ) {
            return 'محدودیت رزرو دوطرفه';
        } else if (
            this.defaultPrice &&
            this.totalReserveCount > this.totalCompartmentCapacity
        ) {
            return 'ظرفیت کوپه';
        }
        return super.buyText;
    }

    maxCompartmentSale = 2;

    get totalCompartmentCapacity() {
        return (
            (this.defaultPrice?.compartmentCapacity ?? 0) *
            this.maxCompartmentSale
        );
    }

    override get reserveError() {
        if (this.tripType !== TripType.RoundTrip && this.isForRoundTrip) {
            return `این بلیط تنها در حالت خرید «رفت و برگشت» قابل انتخاب است.`;
        }
        if (
            this.defaultPrice &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return 'ظرفیت موجود کمتر از تعداد مسافران شماست!';
        }
        const { totalCompartmentCapacity } = this;
        if (
            this.defaultPrice &&
            this.totalReserveCount > totalCompartmentCapacity
        ) {
            return `تعداد مسافرین انتخابی از تعداد ظرفیت دو کوپه بیشتر است.لطفا با تغییر جستجو تعداد مسافرین خود را به ${
                totalCompartmentCapacity
            }نفر تغییر دهید.و برای ${
                this.totalReserveCount - totalCompartmentCapacity
            }مسافر دیگر به صورت جداگانه خرید نمایید.`;
        }
        return super.reserveError;
    }

    override get showReserveErrorInDesktop() {
        return this.tripType !== TripType.RoundTrip && this.isForRoundTrip;
    }

    override checkNeedsRaiseClass(count: PassengerCount) {
        if (this.isForRoundTrip) {
            return false;
        }
        return super.checkNeedsRaiseClass(count);
    }

    override get hasGenderLimitation() {
        const sellCode = this.defaultSellType?.code;
        switch (sellCode) {
            case SellTypeCode.Male:
                return {
                    gender: Gender.Male,
                    ignorable: true,
                    message:
                        'شما در حال خرید از سهمیه برادران هستید. در صورت عدم رعایت قوانین سهمیه، مِستربلیط مسئولیتی در قبال عواقب احتمالی آن برعهده نخواهد داشت.',
                };
            case SellTypeCode.Female:
                return {
                    gender: Gender.Female,
                    ignorable: true,
                    message:
                        'شما در حال خرید از سهمیه خواهران هستید. در صورت عدم رعایت قوانین سهمیه، مِستربلیط مسئولیتی در قبال عواقب احتمالی آن برعهده نخواهد داشت.',
                };
            default:
                return false;
        }
    }

    override get canTrackPackageCapacity(): boolean {
        if (this.defaultSellType?.code === SellTypeCode.Car) {
            return false;
        }
        return super.canTrackPackageCapacity;
    }

    override get reserveButtonEnabled() {
        if (
            this.defaultPrice &&
            this.defaultPrice.capacity < this.totalReserveCount
        ) {
            return false;
        }
        if (this.tripType !== TripType.RoundTrip && this.isForRoundTrip) {
            return false;
        }
        if (this.totalReserveCount > this.capacity) {
            return false;
        }
        if (this.minPersons > this.totalCount) {
            return false;
        }
        if (
            this.defaultPrice &&
            this.totalReserveCount > this.totalCompartmentCapacity
        ) {
            return false;
        }
        return super.reserveButtonEnabled;
    }

    override get order() {
        return new ItineraryOrder(
            this.departureDateTime.getTime(),
            (this.defaultPrice && this.defaultPrice.singlePrice) || 0
        );
    }

    /**
     * default price id of this train package is train capacity id
     */
    get trainCapacityId() {
        return this.defaultPrice!.id;
    }

    override get raisePriceClassesTitle() {
        return 'نوع واگن';
    }

    override get raisePriceTypesTitle() {
        return 'نوع کوپه';
    }

    static revive(data: TrainPackage) {
        const newPack = new TrainPackage();
        Object.assign(newPack, data);
        if (data.selectedPrices?.length) {
            newPack.selectedPrices = [];
            data.selectedPrices.forEach(price => {
                const priceClass = new TrainPriceClass();
                Object.assign(priceClass, price);
                newPack.selectedPrices!.push(priceClass);
            });
        }
        newPack.segments[0].trips[0].departureDateTime = new Date(
            newPack.segments[0].trips[0].departureDateTime
        );
        newPack.segments = newPack.segments.map(
            currentSegment =>
                new TripSegment(
                    currentSegment.trips.map(t =>
                        // the provided data bby priceClass is going to replace by t, it is not that important to assign TrainPriceClass data as well
                        Object.assign(new Train({}, new TrainPriceClass()), t)
                    ),
                    currentSegment.options,
                    currentSegment.stopsDescription,
                    currentSegment.collapseTrips,
                    currentSegment.compactOptions
                )
        );
        return newPack;
    }
}
